import type { SvgIconComponent } from "@mui/icons-material";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Divider,
  Radio,
  Stack,
  Switch,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { yellow } from "@mui/material/colors";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../api/adminApi";
import type { McpPiiProfile, PiiTier } from "../../../api/types/integrationsTypes";
import UnpublishIcon from "@entrilia/shared/icons/UnpublishIcon";

const setPiiProfile = withErrorHandling(adminApi.setMcpPiiProfile);

const restrictionTierRank: Partial<Record<PiiTier, number>> = {
  PersonalContact: 1,
  IdentifyingLabel: 2,
  Operational: 3,
};

const isRestrictionIncluded = (tier: PiiTier, selectedTier: PiiTier) =>
  (restrictionTierRank[selectedTier] ?? 0) >= (restrictionTierRank[tier] ?? 0);

interface Props {
  canManage: boolean;
  onProfileLoaded?: (modifiedBy: string | undefined, modifiedAt: string | undefined) => void;
}

const McpPiiProfileSection = ({ canManage, onProfileLoaded }: Props) => {
  const theme = useTheme();
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [profile, setProfile] = useState<McpPiiProfile | null>(null);
  const [selectedTier, setSelectedTier] = useState<PiiTier | null>(null);
  const [saving, setSaving] = useState(false);

  const [profileResult, fetchError] = useFetch(adminApi.getMcpPiiProfile, {
    onSetData: (data) => {
      setProfile(data);
      setSelectedTier(data.maxAllowedTier);
      onProfileLoaded?.(data.modifiedBy, data.modifiedAt);
    },
  });

  const tiers: { value: PiiTier; label: string; description: string; color: string; Icon: SvgIconComponent }[] = [
    {
      value: "PersonalContact",
      label: "Hide financial credentials",
      description: "Hides SSN/EIN, GIIN, IBAN, bank account numbers, SWIFT/ABA codes and tax forms.",
      color: theme.palette.warning.main,
      Icon: WarningAmberRoundedIcon,
    },
    {
      value: "IdentifyingLabel",
      label: "Hide contact information",
      description: "Also hides addresses, emails, phone numbers and other personal contact details.",
      color: yellow[800],
      Icon: UnpublishIcon,
    },
    {
      value: "Operational",
      label: "Hide names & identifiers",
      description: "Also hides investor and contact names.",
      color: theme.palette.success.main,
      Icon: AdminPanelSettingsOutlinedIcon,
    },
  ];

  const dirty = profile !== null && selectedTier !== null && selectedTier !== profile.maxAllowedTier;
  const fullAccessEnabled = selectedTier === "HighlySensitive";
  const disabled = !canManage || saving;

  const handleCancel = () => {
    if (profile) {
      setSelectedTier(profile.maxAllowedTier);
    }
  };

  const handleSave = async () => {
    if (!profile || !selectedTier || !dirty) {
      return;
    }

    setSaving(true);
    const [response, error] = await setPiiProfile({
      maxAllowedTier: selectedTier,
      allowOverrides: profile.allowOverrides,
      denyOverrides: profile.denyOverrides,
    });

    if (!error) {
      setProfile(response);
      setSelectedTier(response.maxAllowedTier);
      onProfileLoaded?.(response.modifiedBy, response.modifiedAt);
      sendNotification("Sensitive data controls updated.");
    } else {
      logError(error, "[McpPiiProfileSection] setMcpPiiProfile");
      sendNotificationError("Failed to update sensitive data controls.");
    }
    setSaving(false);
  };

  const handleFullAccessChange = (checked: boolean) => {
    setSelectedTier(checked ? "HighlySensitive" : "PersonalContact");
  };

  if (fetchError) {
    return <Alert severity="error">Could not load sensitive data controls.</Alert>;
  }

  if (!profileResult || !profile || selectedTier === null) {
    return <InlineLoader />;
  }

  return (
    <Stack spacing={2} sx={{ maxWidth: "md" }}>
      <Stack spacing={1}>
        <Typography variant="subtitle1">Sensitive data controls</Typography>
        <Typography variant="body2" color="textSecondary">
          Choose what the MCP returns to AI clients. Each level adds restrictions on top of the level above. This
          setting only affects MCP responses and does not change what users see inside Entrilia.
        </Typography>
        <Alert severity="info" icon={<InfoOutlinedIcon fontSize="small" />} sx={{ py: 0.5 }}>
          This applies to Entrilia&apos;s standard fields only. Custom dimensions you create are not classified and
          remain visible at every level — contact us to hide specific ones.
        </Alert>
      </Stack>
      <Stack spacing={1.5}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 1.5, py: 1 }}>
          <Switch
            checked={fullAccessEnabled}
            disabled={disabled}
            color="primary"
            slotProps={{ input: { "aria-label": "Full access" } }}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => handleFullAccessChange(e.target.checked)}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body1">Full access</Typography>
            <Typography variant="caption" color="textSecondary" sx={{ display: "block" }}>
              Returns all data, including SSN/EIN, GIIN, bank accounts, IBAN, SWIFT/ABA codes, addresses and contact
              details.
            </Typography>
          </Box>
        </Box>

        {!fullAccessEnabled && (
          <Stack spacing={1.5}>
            <Divider sx={{ borderStyle: "dashed" }} />
            {tiers.map((tier) => {
              const isSelected = selectedTier === tier.value;
              const included = isRestrictionIncluded(tier.value, selectedTier);
              const TierIcon = tier.Icon;
              return (
                <Card
                  key={tier.value}
                  variant="outlined"
                  onClick={() => {
                    if (!disabled) {
                      setSelectedTier(tier.value);
                    }
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 1.5,
                    py: 1,
                    cursor: disabled ? "default" : "pointer",
                    bgcolor: isSelected ? "action.hover" : "background.paper",
                    transition: "background-color 120ms ease",
                    "&:hover": disabled ? undefined : { bgcolor: isSelected ? "action.hover" : "action.hover" },
                  }}
                >
                  <Avatar variant="rounded" sx={{ bgcolor: alpha(tier.color, 0.12) }}>
                    <TierIcon sx={{ color: tier.color }} />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
                      <Typography variant="body1">{tier.label}</Typography>
                      {included && (
                        <Tooltip title={"Also applied by the selected option"} arrow placement="top">
                          <CheckRoundedIcon sx={{ color: theme.palette.success.main, fontSize: 16 }} />
                        </Tooltip>
                      )}
                    </Stack>
                    <Typography variant="caption" color="textSecondary" sx={{ display: "block" }}>
                      {tier.description}
                    </Typography>
                  </Box>
                  <Radio
                    checked={isSelected}
                    disabled={disabled}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => setSelectedTier(tier.value)}
                  />
                </Card>
              );
            })}
          </Stack>
        )}
      </Stack>
      <Stack direction="row" spacing={1}>
        <Button variant="contained" color="primary" disabled={!canManage || !dirty || saving} onClick={handleSave}>
          Save changes
        </Button>
        <Button variant="text" color="secondary" disabled={!canManage || !dirty || saving} onClick={handleCancel}>
          Cancel
        </Button>
      </Stack>
    </Stack>
  );
};

export default McpPiiProfileSection;
